import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { TableChartOutlined, DescriptionOutlined, MedicalServicesOutlined, MedicationOutlined, PolicyOutlined } from '@mui/icons-material';
import { supportArticleRoute } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { HelpCenterContainer, HelpCenterTile } from '@uplift/pages';
import { ProviderMedicalRole } from '@uplift/types';
import { ProviderLayout, ProviderLayoutPageHeader, ProviderLayoutPageTitle } from '@uplift/ui-v2';
import { SUPPORT_ARTICLE_SLUGS } from './articles';
import { SupportArticleIndex } from './support-article-index';

const getProviderTiles = (documentationGuideHref: string, treatTile: HelpCenterTile): HelpCenterTile[] => [
  treatTile,
  {
    title: 'CPT Coding Guide',
    description: 'Codes and modifiers for psychiatric visits.',
    icon: TableChartOutlined,
    href: supportArticleRoute.createLink({ slug: SUPPORT_ARTICLE_SLUGS.cptCodingGuide }),
  },
  {
    title: 'Documentation Guide',
    description: 'How to chart notes and visit summaries.',
    icon: DescriptionOutlined,
    href: documentationGuideHref,
  },
  {
    title: 'Formulary',
    description: 'Covered medications and prescribing tiers.',
    icon: MedicationOutlined,
    href: supportArticleRoute.createLink({ slug: SUPPORT_ARTICLE_SLUGS.formulary }),
  },
  {
    title: 'Prescribing Policy',
    description: 'Controlled substance and Rx requirements.',
    icon: PolicyOutlined,
    href: supportArticleRoute.createLink({ slug: SUPPORT_ARTICLE_SLUGS.prescribingPolicy }),
  },
];

export const SupportPage = () => {
  const l10n = useL10n();
  const me = useCurrentuser();

  const tiles = useMemo(() => {
    const documentationGuideHref = me.medicalRole === ProviderMedicalRole.psychiatrist
      ? supportArticleRoute.createLink({ slug: SUPPORT_ARTICLE_SLUGS.psychiatryDocumentationGuide })
      : supportArticleRoute.createLink({ slug: SUPPORT_ARTICLE_SLUGS.therapyDocumentationGuide });

    const treatTile: HelpCenterTile = {
      title: l10n.support.treatTileTitle,
      description: l10n.support.treatTileDescription,
      icon: MedicalServicesOutlined,
      href: l10n.support.treatUrl,
    };

    return getProviderTiles(documentationGuideHref, treatTile);
  }, [ l10n.support.treatTileDescription, l10n.support.treatTileTitle, l10n.support.treatUrl, me.medicalRole ]);

  return (
    <ProviderLayout>
      <ProviderLayoutPageHeader
        title={(
          <Box>
            <ProviderLayoutPageTitle title={l10n.support.pageTitle} />
            <Typography variant="body1" color="textSecondary">
              {l10n.support.pageSubtitle}
            </Typography>
          </Box>
        )}
      />
      <HelpCenterContainer
        tiles={tiles}
        maxColumns={5}
        articleIndex={<SupportArticleIndex />}
      />
    </ProviderLayout>
  );
};
